import { Button, Tooltip } from "@xola/ui-kit";
import { uniqueId } from "lodash";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Experience, Guest } from "types";
import { isOrganizerSelected } from "../../models/Guest";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { SecondaryButton } from "../Common/SecondaryButton";
import { ContentLayout } from "../Layout/ContentLayout";
import { CheckoutButton } from "./CheckoutButton";
import { CreatePurchaseExperienceFields } from "./CreatePurchaseExperienceFields";
import { CreatePurchaseFooter } from "./CreatePurchaseFooter";
import { CreatePurchaseSidebar } from "./CreatePurchaseSidebar";
import { getPurchaseItemsHavingId, handleGuestListChange } from "./helpers/PurchaseCreateHelpers";
import { getExperienceValidations } from "./hooks/getExperienceValidations";
import { getCreatePurchasePackageFields } from "./hooks/useCreatePurchasePackageFields";

interface CreatePurchaseExperienceScreenProps {
    experience: Experience;
    onSubmit: () => void;
}

export const CreatePurchaseExperienceScreen = ({ experience, onSubmit }: CreatePurchaseExperienceScreenProps) => {
    const history = useHistory();
    const isVariableDemographicsPackage = useCreatePurchaseStore((state) => state.isVariableDemographicsPackage());

    const resetState = useCreatePurchaseStore((state) => state.actions.resetState);

    const popoverParentId = uniqueId("popoverParentId");

    useEffect(() => {
        return () => {
            const isSingleItemPurchase = useCreatePurchaseStore.getState().purchase.items.length === 1;
            if (history.action === "POP" && isSingleItemPurchase) {
                /** User has clicked on a product in create purchase flow and clicked browser back button, so we need to reset the CreatePurchase state to initial */
                resetState();
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="grid h-full grid-cols-12" id={popoverParentId}>
            <ContentLayout isWide classNames={{ root: "relative col-span-6 h-full 2xl:col-span-7" }}>
                <h3 className="mb-6">{isVariableDemographicsPackage ? "Experience Details" : "Product Details"}</h3>

                {isVariableDemographicsPackage && (
                    <div className="mb-3 w-full rounded bg-gray-lighter p-2">
                        <div className="flex items-center space-x-1">
                            <div className="shrink-0">
                                <ProductImage product={experience} size="small" className="!mr-1" />
                            </div>

                            <h4 className="font-bold">
                                <NoTranslate>{experience.name}</NoTranslate>
                            </h4>
                        </div>
                    </div>
                )}

                <CreatePurchaseExperienceFields showQuestionnaireList experience={experience} />

                <CreateExperiencePurchaseFooter onSubmit={onSubmit} />
            </ContentLayout>

            <div className="col-span-6 shrink-0 space-y-6 bg-gray-lighter 2xl:col-span-5">
                <CreatePurchaseSidebar />
            </div>
        </div>
    );
};

const CreateExperiencePurchaseFooter = ({ onSubmit }: { onSubmit: () => void }) => {
    const history = useHistory();
    useCreatePurchaseStore((state) => state.purchase);
    const item = useCreatePurchaseStore((state) => state.getActiveItem());

    const activeExperience = useCreatePurchaseStore((state) => state.getActiveProduct());
    const setActiveItemIndex = useCreatePurchaseStore((state) => state.actions.setActiveItemIndex);
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);
    const resetState = useCreatePurchaseStore((state) => state.actions.resetState);

    const { showNextButton } = getCreatePurchasePackageFields();
    const { getDisabledReasons } = getExperienceValidations();
    const disabledReasons = getDisabledReasons();
    const shouldDisableFooter = useCreatePurchaseStore((state) => !state.purchaseId);
    const isVariableDemographicsPackage = useCreatePurchaseStore((state) => state.isVariableDemographicsPackage());

    const handleCancel = () => {
        resetState();

        history.push("/cart/init");
    };

    const handleAddNewProductClick = () => {
        setPurchase((draft) => getPurchaseItemsHavingId(draft));

        history.push("/cart/init", { isAddingItem: true });
    };

    const handleNext = () => {
        const { purchase } = useCreatePurchaseStore.getState();
        const index = purchase.items.findIndex((item) => item.product.id === activeExperience.id);
        setActiveItemIndex(index + 1);

        const createPurchaseScreenLayout = document.querySelector(`#columnLayoutFill`);
        if (createPurchaseScreenLayout) {
            (createPurchaseScreenLayout as HTMLElement).scroll(0, 0);
        }
    };

    const handleCheckout = (): any => {
        if (item.guests?.length === 1 && !isOrganizerSelected(item.guests)) {
            setMainGuest(item.guests);
        }

        onSubmit();
    };

    return (
        <CreatePurchaseFooter secondaryText="Cancel" onCancel={handleCancel}>
            {disabledReasons.length > 0 ? (
                <Tooltip
                    content={disabledReasons.map((message, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index}>{message}</div>
                    ))}
                >
                    <CheckoutButton disabled label={showNextButton ? "Next" : "Checkout"} />
                </Tooltip>
            ) : showNextButton ? (
                <Button disabled={shouldDisableFooter} onClick={handleNext}>
                    Next
                </Button>
            ) : (
                <CheckoutButton disabled={shouldDisableFooter} onClick={handleCheckout} />
            )}

            {!isVariableDemographicsPackage && (
                <SecondaryButton disabled={shouldDisableFooter} onClick={handleAddNewProductClick}>
                    Add Another Product
                </SecondaryButton>
            )}
        </CreatePurchaseFooter>
    );
};

const setMainGuest = (guests: Guest[]) => {
    handleGuestListChange(
        guests.map((guest) => {
            return { ...guest, primary: true };
        }),
    );
};
